import React, { useEffect, useMemo, useState } from 'react';
import { Country, State } from 'country-state-city';
import { peekCheckoutGeo, useCheckoutGeo } from '../hooks/useCheckoutGeo';

const DEFAULT_COPY = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  cardNumber: 'Card number',
  expiry: 'Expiry',
  cvv: 'CVV',
  country: 'Country',
  state: 'State',
  province: 'Province',
  stateOrProvince: 'State / province',
  zip: 'ZIP',
  postalCode: 'Postal code',
  selectCountry: 'Select country',
  selectRegion: 'Select',
  fillPaymentFields: 'Please fill in all payment fields.',
  invalidCard: 'Enter a valid card number.',
  invalidExpiry: 'Enter a valid expiry (MM/YY).',
  invalidCvv: 'Enter a valid CVV.',
  payNow: 'Signup Now',
  processing: 'Processing...',
};

function regionLabel(countryCode, copy) {
  if (countryCode === 'US') return copy.state;
  if (countryCode === 'CA') return copy.province;
  return copy.stateOrProvince;
}

function postalLabel(countryCode, copy) {
  return countryCode === 'US' ? copy.zip : copy.postalCode;
}

function formatCardNumber(value) {
  const digits = value.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function formatExpiry(value) {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

function isValidExpiry(value) {
  const match = value.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;
  const month = Number(match[1]);
  const year = Number(match[2]);
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;
  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;
  return true;
}

function countryFromGeo(location) {
  const iso = String(location?.country || '').toUpperCase();
  if (!iso) return '';
  const known = Country.getAllCountries().some((c) => c.isoCode === iso);
  return known ? iso : '';
}

function regionFromGeo(location, countryCode) {
  if (!countryCode) return '';
  const states = State.getStatesOfCountry(countryCode) || [];
  const code = String(location?.regionCode || '').toUpperCase();
  if (code) {
    const byCode = states.find((s) => s.isoCode.toUpperCase() === code);
    if (byCode) return byCode.isoCode;
  }
  const name = String(location?.region || '').trim().toLowerCase();
  if (name) {
    const byName = states.find((s) => s.name.toLowerCase() === name);
    if (byName) return byName.isoCode;
  }
  return '';
}

export function CheckoutPaymentForm({
  email,
  accountLabel = 'Account:',
  copy: copyOverrides,
  hideRegion = false,
  error,
  disabled,
  onPay,
}) {
  const copy = { ...DEFAULT_COPY, ...copyOverrides };
  const { loading: geoLoading, payload: geoPayload } = useCheckoutGeo();
  const countries = useMemo(() => {
    const all = Country.getAllCountries().slice().sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const preferred = ['US', 'CA', 'GB', 'AU'];
    const head = preferred
      .map((code) => all.find((c) => c.isoCode === code))
      .filter(Boolean);
    const rest = all.filter((c) => !preferred.includes(c.isoCode));
    return [...head, ...rest];
  }, []);

  const initialLocation = peekCheckoutGeo()?.location;
  const initialCountry = countryFromGeo(initialLocation) || 'US';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [countryCode, setCountryCode] = useState(initialCountry);
  const [regionCode, setRegionCode] = useState(() =>
    regionFromGeo(initialLocation, initialCountry)
  );
  const [postal, setPostal] = useState('');
  const [localError, setLocalError] = useState(null);
  const [geoApplied, setGeoApplied] = useState(() =>
    Boolean(countryFromGeo(initialLocation))
  );

  const states = useMemo(
    () => State.getStatesOfCountry(countryCode) || [],
    [countryCode]
  );
  const showRegion = !hideRegion && states.length > 0;

  useEffect(() => {
    if (geoLoading || geoApplied) return;
    const location = geoPayload?.location;
    const nextCountry = countryFromGeo(location);
    if (nextCountry) {
      setCountryCode(nextCountry);
      setRegionCode(regionFromGeo(location, nextCountry));
    }
    setGeoApplied(true);
  }, [geoLoading, geoPayload, geoApplied]);

  const handleCountryChange = (nextCode) => {
    setCountryCode(nextCode);
    setRegionCode('');
    setGeoApplied(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError(null);

    const digits = cardNumber.replace(/\D/g, '');
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !countryCode ||
      !postal.trim() ||
      (showRegion && !regionCode)
    ) {
      setLocalError(copy.fillPaymentFields);
      return;
    }
    if (digits.length < 13 || digits.length > 19) {
      setLocalError(copy.invalidCard);
      return;
    }
    if (!isValidExpiry(expiry)) {
      setLocalError(copy.invalidExpiry);
      return;
    }
    if (!/^\d{3,4}$/.test(cvv)) {
      setLocalError(copy.invalidCvv);
      return;
    }

    const country = countries.find((c) => c.isoCode === countryCode);
    const region = states.find((s) => s.isoCode === regionCode);
    onPay?.({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      cardNumber: digits,
      expiry,
      cvv,
      countryCode,
      countryName: country?.name || '',
      regionCode: regionCode || '',
      regionName: region?.name || '',
      postal: postal.trim(),
    });
  };

  const displayError = error || localError;

  return (
    <form className="checkout-payment-form" onSubmit={handleSubmit}>
      <div className="checkout-form-section">
        {email ? (
          <>
            <label className="checkout-label" htmlFor="checkout-email">
              {copy.emailLabel || copy.email}
            </label>
            <input
              id="checkout-email"
              className="checkout-input"
              type="email"
              value={email}
              readOnly
              autoComplete="email"
            />
          </>
        ) : null}

        <div className="checkout-field-row checkout-field-row--inline">
          <div className="checkout-field">
            <label className="checkout-label" htmlFor="checkout-first-name">
              {copy.firstName} <span className="checkout-required">*</span>
            </label>
            <input
              id="checkout-first-name"
              className="checkout-input"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="checkout-field">
            <label className="checkout-label" htmlFor="checkout-last-name">
              {copy.lastName} <span className="checkout-required">*</span>
            </label>
            <input
              id="checkout-last-name"
              className="checkout-input"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
        </div>

        {hideRegion ? (
          <div className="checkout-field-row checkout-field-row--region">
            <div className="checkout-field">
              <label className="checkout-label" htmlFor="checkout-country">
                {copy.country} <span className="checkout-required">*</span>
              </label>
              <select
                id="checkout-country"
                className="checkout-input checkout-select"
                autoComplete="country"
                value={countryCode}
                onChange={(e) => handleCountryChange(e.target.value)}
                required
              >
                <option value="">{copy.selectCountry}</option>
                {countries.map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="checkout-field">
              <label className="checkout-label" htmlFor="checkout-postal">
                {postalLabel(countryCode, copy)}{' '}
                <span className="checkout-required">*</span>
              </label>
              <input
                id="checkout-postal"
                className="checkout-input"
                autoComplete="postal-code"
                value={postal}
                onChange={(e) => setPostal(e.target.value)}
                required
              />
            </div>
          </div>
        ) : (
          <>
            <label className="checkout-label" htmlFor="checkout-country">
              {copy.country} <span className="checkout-required">*</span>
            </label>
            <select
              id="checkout-country"
              className="checkout-input checkout-select"
              autoComplete="country"
              value={countryCode}
              onChange={(e) => handleCountryChange(e.target.value)}
              required
            >
              <option value="">{copy.selectCountry}</option>
              {countries.map((country) => (
                <option key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </select>

            <div className={`checkout-field-row${showRegion ? ' checkout-field-row--region' : ' checkout-field-row--single'}`}>
              {showRegion ? (
                <div className="checkout-field">
                  <label className="checkout-label" htmlFor="checkout-region">
                    {regionLabel(countryCode, copy)}{' '}
                    <span className="checkout-required">*</span>
                  </label>
                  <select
                    id="checkout-region"
                    className="checkout-input checkout-select"
                    autoComplete="address-level1"
                    value={regionCode}
                    onChange={(e) => setRegionCode(e.target.value)}
                    required
                  >
                    <option value="">{copy.selectRegion}</option>
                    {states.map((state) => (
                      <option key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="checkout-field">
                <label className="checkout-label" htmlFor="checkout-postal">
                  {postalLabel(countryCode, copy)}{' '}
                  <span className="checkout-required">*</span>
                </label>
                <input
                  id="checkout-postal"
                  className="checkout-input"
                  autoComplete="postal-code"
                  value={postal}
                  onChange={(e) => setPostal(e.target.value)}
                  required
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div className="checkout-form-section">
        <label className="checkout-label" htmlFor="checkout-card-number">
          {copy.cardNumber} <span className="checkout-required">*</span>
        </label>
        <input
          id="checkout-card-number"
          className="checkout-input"
          inputMode="numeric"
          autoComplete="cc-number"
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          required
        />

        <div className="checkout-field-row checkout-field-row--inline">
          <div className="checkout-field">
            <label className="checkout-label" htmlFor="checkout-expiry">
              {copy.expiry} <span className="checkout-required">*</span>
            </label>
            <input
              id="checkout-expiry"
              className="checkout-input"
              inputMode="numeric"
              autoComplete="cc-exp"
              placeholder="MM/YY"
              value={expiry}
              onChange={(e) => setExpiry(formatExpiry(e.target.value))}
              required
            />
          </div>
          <div className="checkout-field">
            <label className="checkout-label" htmlFor="checkout-cvv">
              {copy.cvv} <span className="checkout-required">*</span>
            </label>
            <input
              id="checkout-cvv"
              className="checkout-input"
              inputMode="numeric"
              autoComplete="cc-csc"
              placeholder="123"
              maxLength={4}
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
              required
            />
          </div>
        </div>
      </div>

      {displayError ? (
        <p className="checkout-error">{displayError}</p>
      ) : null}

      <button
        type="submit"
        className="checkout-btn-primary"
        disabled={disabled}
        style={{ marginTop: '4px', fontSize: '16px' }}
      >
        {disabled ? copy.processing || copy.loading || copy.payNow : copy.payNow}
      </button>
    </form>
  );
}
